import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, ChevronDown, ChevronRight, Download, Play } from 'lucide-react';

import {
  createOpsAgentRun,
  getOpsAgentRun,
  listOpsAgentRuns,
  opsAgentRunCsvUrl,
  type CsOpsAgentRun,
} from '@/api/cs/operations.api';
import { CsInlineError, TEXTAREA_CLS } from '@/components/cs/PageStates';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

/**
 * Operations → Ops Agent (spec §3.10.6). Ask a question → POST /runs
 * (202 returns the 'running' row) → poll GET /runs/:id until the
 * status leaves 'running' (TanStack refetchInterval) → render the
 * findings as pre-wrap text + a collapsible per-tool-call trace +
 * a CSV export link. Read-only agent — it can never write.
 */

const POLL_INTERVAL_MS = 1500;

export function OpsAgentTab() {
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ['cs', 'ops-agent', 'runs'],
    queryFn: () => listOpsAgentRuns(1, 20),
  });

  const activeRunQuery = useQuery({
    queryKey: ['cs', 'ops-agent', 'run', activeRunId],
    queryFn: () => getOpsAgentRun(activeRunId ?? ''),
    enabled: activeRunId !== null,
    // Poll while running; stop as soon as the run completes or fails.
    refetchInterval: (query) => (query.state.data?.status === 'running' ? POLL_INTERVAL_MS : false),
  });

  const createMutation = useMutation({
    mutationFn: () => createOpsAgentRun(question.trim()),
    onSuccess: (run) => {
      setActiveRunId(run.id);
      setQuestion('');
      void queryClient.invalidateQueries({ queryKey: ['cs', 'ops-agent', 'runs'] });
    },
  });

  const activeRun = activeRunQuery.data ?? null;

  return (
    <div className="space-y-4">
      <Card className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <Bot className="size-5 text-kw-cerulean" />
          <h2 className="text-[14px] font-medium text-kw-shark">Ask the operations agent</h2>
        </div>
        <p className="text-[13px] text-kw-muted">
          Read-only analysis over CS data (tickets, agents, routing, SLA, errors). Every tool call is audited;
          the agent cannot change anything.
        </p>
        <textarea
          aria-label="Ops agent question"
          value={question}
          onChange={(e) => {
            setQuestion(e.target.value);
          }}
          rows={3}
          placeholder="e.g. Which agents did the most manual reassignments this week, and on which tickets?"
          className={TEXTAREA_CLS}
        />
        <CsInlineError error={createMutation.error} fallback="Could not start the run." />
        <div className="flex justify-end">
          <Button
            isLoading={createMutation.isPending}
            disabled={question.trim().length < 4}
            onClick={() => {
              createMutation.mutate();
            }}
          >
            <Play className="size-4" />
            Run
          </Button>
        </div>
      </Card>

      {activeRun ? (
        <RunResult
          run={activeRun}
          onFinishedRefresh={() => {
            void queryClient.invalidateQueries({ queryKey: ['cs', 'ops-agent', 'runs'] });
          }}
        />
      ) : null}

      <section>
        <h2 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Run history</h2>
        <Card className="mt-2 p-0">
          {(runsQuery.data?.data ?? []).length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-kw-muted">No runs yet.</p>
          ) : (
            <ul className="divide-y divide-kw-border">
              {(runsQuery.data?.data ?? []).map((run) => (
                <li key={run.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveRunId(run.id);
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5 text-left text-[13px] hover:bg-kw-bg',
                      run.id === activeRunId && 'bg-kw-cerulean-light/30',
                    )}
                  >
                    <RunStatusBadge status={run.status} />
                    <span className="min-w-0 flex-1 truncate text-kw-shark">{run.prompt}</span>
                    <span className="shrink-0 text-[12px] text-kw-muted">
                      {new Date(run.startedAt).toLocaleString()}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    </div>
  );
}

function RunStatusBadge({ status }: { status: CsOpsAgentRun['status'] }) {
  if (status === 'running') return <Badge variant="info">Running</Badge>;
  if (status === 'completed') return <Badge variant="success">Completed</Badge>;
  return <Badge variant="danger">Failed</Badge>;
}

// ─────────────────────────────────────────────────────────────────────────
// Result panel
// ─────────────────────────────────────────────────────────────────────────

function RunResult({ run, onFinishedRefresh }: { run: CsOpsAgentRun; onFinishedRefresh: () => void }) {
  const [traceOpen, setTraceOpen] = useState(false);

  // One history refresh once the poll sees a terminal status.
  const status = run.status;
  const runId = run.id;
  useEffect(() => {
    if (status !== 'running') onFinishedRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh once per run+status transition
  }, [runId, status]);

  return (
    <Card className="space-y-3 p-4" data-testid="cs-ops-run-result">
      <div className="flex flex-wrap items-center gap-2">
        <RunStatusBadge status={run.status} />
        <p className="min-w-0 flex-1 truncate text-[13px] font-medium text-kw-shark">{run.prompt}</p>
        {run.status === 'completed' ? (
          <a
            href={opsAgentRunCsvUrl(run.id)}
            download
            className="inline-flex items-center gap-1 text-[13px] text-kw-cerulean hover:text-kw-cerulean-dark"
          >
            <Download className="size-4" />
            Export CSV
          </a>
        ) : null}
      </div>

      {run.status === 'running' ? (
        <div className="flex items-center gap-3 py-4">
          <div className="size-5 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
          <p className="text-[13px] text-kw-muted">
            The agent is working — this panel refreshes automatically.
          </p>
        </div>
      ) : run.status === 'failed' ? (
        <p className="text-[13px] text-kw-danger-text">{run.error ?? 'The run failed.'}</p>
      ) : (
        <p className="whitespace-pre-wrap text-[13px] text-kw-shark">
          {run.findingsMarkdown ?? 'The run completed without findings.'}
        </p>
      )}

      {run.toolCallsJson.length > 0 ? (
        <div>
          <button
            type="button"
            aria-expanded={traceOpen}
            onClick={() => {
              setTraceOpen((v) => !v);
            }}
            className="inline-flex items-center gap-1 text-[13px] font-medium text-kw-muted hover:text-kw-shark"
          >
            {traceOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
            Tool calls ({String(run.toolCallsJson.length)})
          </button>
          {traceOpen ? (
            <ul className="mt-2 space-y-2">
              {run.toolCallsJson.map((call, i) => (
                <li key={i} className="rounded-kw-md border border-kw-border bg-kw-bg/50 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-[12px]">
                    <span className="font-mono font-medium text-kw-shark">{call.name ?? 'tool'}</span>
                    {call.rowCount !== null && call.rowCount !== undefined ? (
                      <span className="text-kw-muted">{String(call.rowCount)} row(s)</span>
                    ) : null}
                    {call.error ? <span className="text-kw-danger-text">{call.error}</span> : null}
                  </div>
                  <pre className="mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-[11px] text-kw-muted">
                    {JSON.stringify(call.input ?? {}, null, 2)}
                  </pre>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
